import CheckoutContext from '@finn/auto-ui/contexts/Checkout';
import { useClosedUserGroup } from '@finn/auto-ui/contexts/ClosedUserGroup';
import { ToggleGroup, ToggleItem } from '@finn/design-system/atoms/toggle';
import { BASE_SUBSCRIPTION_PATH } from '@finn/ua-constants';
import { Features, useIsABVariant } from '@finn/ua-featureflags';
import {
  Filters,
  FILTERS_RESPONSE_SWR_FALLBACK_KEY,
  GenericVehicleDetails,
  getVehicleSize,
  parseFilterValues,
  PathFilterProvider,
  ProductCard,
  ProductGroupBy,
  usePartnerDiscount,
  useProductListItems,
} from '@finn/ua-vehicle';
import { CTA } from '@finn/ui-components';
import { NumericHeadingSizes, Slider } from '@finn/ui-modules';
import { useCurrentLocale } from '@finn/ui-utils';
import { usePathname } from 'next/navigation';
import { useContext, useMemo } from 'react';
import { SWRConfig } from 'swr';

import vehicleSizes from '~/localization/product_sizes.json';
import { CTAData } from '~/types/UIModules';

import cachedFilters from '../../localization/available.filters.json';
import { useProductSliderToggles } from './hooks/useProductSlidersToggle';

type Props = {
  title?: string;
  headingStyle?: NumericHeadingSizes;
  filter?: Filters;
  cta?: CTAData;
  baseURL?: string;
  urlParams?: string;
  isForIframe?: boolean;
  isInPLP?: boolean;
  hidePrice?: boolean;
  forceB2BPrice?: boolean;
  vehicles?: GenericVehicleDetails[];
};

const ProductSliderComponent = ({
  title,
  headingStyle,
  filter = {},
  cta,
  baseURL,
  urlParams,
  isForIframe = false,
  isInPLP = false,
  hidePrice = false,
  forceB2BPrice = false,
  vehicles,
}: Props) => {
  const { closedUserGroupId } = useClosedUserGroup();
  const { cartInfo } = useContext(CheckoutContext);
  const partnerDiscount = usePartnerDiscount({
    closedUserGroupId,
    cartInfo,
  });

  const { getSliderToggles, handleToggleClick, filters } =
    useProductSliderToggles({
      initialFilters: parseFilterValues(filter),
    });

  const pathname = usePathname();
  const { locale } = useCurrentLocale();
  const isHomePage =
    pathname === '/' ||
    pathname === `/${locale}` ||
    pathname.startsWith('/ab/[...slug]');

  const isSliderTogglesExp =
    useIsABVariant(Features.ExpHPSliderToggles) && isHomePage;

  const SliderToggles = useMemo(() => {
    if (!isSliderTogglesExp) {
      return null;
    }

    const toggles = getSliderToggles();

    return (
      <div className="container mb-6 flex w-full">
        <ToggleGroup
          className="flex flex-wrap gap-2"
          orientation="horizontal"
          data-testid="toggle"
          defaultValue={toggles.default}
          onValueChange={handleToggleClick}
        >
          {toggles.data.map((toggle) => (
            <ToggleItem
              titleStyle="regular"
              className="h-11 py-0"
              key={toggle.text}
              title={toggle.text}
              value={toggle.value}
              variant="regular"
            />
          ))}
        </ToggleGroup>
      </div>
    );
  }, [isSliderTogglesExp, getSliderToggles, handleToggleClick]);

  const { products } = useProductListItems(
    {
      filters,
      groupBy: ProductGroupBy.BRAND_MODEL,
    },
    {
      fallbackData: { results: vehicles, offset: 0, total_results: 0 },
      revalidateOnMount: true,
    }
  );

  return (
    <>
      {products.length > 0 && (
        <Slider
          toggles={SliderToggles}
          title={isSliderTogglesExp ? 'Unsere Autos' : title}
          verticalSpacing={3}
          titleHeadingSize={headingStyle}
          isForIframe={isForIframe || isInPLP}
          isHomePage={isHomePage}
        >
          {products.map((vehicle, idx) => (
            <div
              key={vehicle.id}
              data-productid={vehicle.id}
              data-testid="product-card"
              className="w-[280px] sm:w-[360px]"
            >
              <ProductCard
                vehicleSize={getVehicleSize(vehicle, vehicleSizes)}
                vehicle={vehicle}
                parentTitle={title}
                shouldPreload
                shouldShowPlaceholder={false}
                position={idx + 1}
                baseURL={baseURL}
                urlParams={urlParams}
                isForIframe={isForIframe}
                hidePrice={hidePrice}
                forceB2BPrice={forceB2BPrice}
                partnerDiscount={partnerDiscount}
              />
            </div>
          ))}
        </Slider>
      )}
      {cta && (
        <div className="container">
          <div className="mt-12 sm:mt-14">
            <CTA data={cta.metadata} />
          </div>
        </div>
      )}
    </>
  );
};

const ProductSlider = (props: Props) => {
  const swrConfigValue = useMemo(() => {
    return {
      fallback: {
        [FILTERS_RESPONSE_SWR_FALLBACK_KEY]: cachedFilters['de-DE'],
      },
    };
  }, []);

  return (
    <SWRConfig value={swrConfigValue}>
      <PathFilterProvider basePathname={BASE_SUBSCRIPTION_PATH}>
        <ProductSliderComponent {...props} />
      </PathFilterProvider>
    </SWRConfig>
  );
};

export default ProductSlider;
